import React, {
	useMemo,
	useState,
	useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../stores/types/types';
import type { Shot } from '../../../stores/types/shots';
import { TeleprompterInterface, TeleprompterSectionInterface } from '../../../types/interfaces';
import {
	TELEPROMPTER_SECTION_TYPES,
	TELEPROMPTER_SECTION_STATES,
} from '../../../types/constants';
import type { MediaItem, PresentedMediaItem } from '../../../stores/types/media';
import Socket from '../../../connection/socket';
import * as TeleprompterActions from '../../../stores/actions/teleprompters';
import TeleprompterSection from './teleprompter-section';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import useTeleprompters from '../../../hooks/use-teleprompters';
import ShareWith, { SHARE_TYPE } from '../../general-components/share-with';
import { listToArray } from '../../../utils/utils';
import { RawDraftContentState } from 'draft-js';
import logger from '../../../utils/logger-rum';

interface Props {
	socket: Socket | null;
}

const cleanContent = (content?: string): RawDraftContentState | undefined => {
	// GREEN-6480 - some content returns unicode characters from note
	// auto generation - so we clean the unicode characters before parsing
	if (!content) return;
	let cleanedContent;
	try {
		cleanedContent = JSON.parse(content);
	} catch(e) {
		try {
			cleanedContent = JSON.parse(content
				?.replace(/[\u0010-\u0014]/g, (c: string) => '')
				?.replace(/\u0019/g, (c: string) => '\'')); // this was a single quote in the document from 6480 ticket
		} catch (e) {
			logger.error('error parsing content', e);
			return;
		}
	}
	return cleanedContent;
}

// this functional component controls if we are showing the teleprompter for
// all shots or a specific media
export default function TeleprompterEditor(props: Props) {
	const dispatch = useDispatch();
	const { socket } = props;
	const {
		editTeleprompter,
		handleSetMediaTeleprompter,
	} = useTeleprompters(socket);

	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector((s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const teleprompters = useSelector((s: GS) => s.TeleprompterReducer.teleprompters);
	const selectedTeleprompters = useSelector((s: GS) => s.TeleprompterReducer.selectedTeleprompters);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const host = useSelector((s: GS) => s.SessionReducer.host);

	const selectedTeleprompter = useMemo(() => {
		if (selectedTeleprompters.media) {
			return teleprompters.find(
				(t: TeleprompterInterface) => t.id === selectedTeleprompters.media,
			) ?? null;
		}
		return teleprompters.find(
			(t: TeleprompterInterface) => t.id === selectedTeleprompters.shot,
		) ?? null;
	}, [teleprompters, selectedTeleprompters]);

	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === selectedTeleprompter?.media_id) ?? null,
		[media, selectedTeleprompter],
	);

	const [teleprompterTitle, setTeleprompterTitle] = useState(selectedTeleprompter?.title ?? '');
	const [selectedSection, setSelectedSection] = useState<number | string>(0);

	// reset scroll position if slide deck is not in canvas
	useEffect(() => {
		if (
			mediaItem
			&& !presentedMedia.some((item: PresentedMediaItem) => item.mediaId === mediaItem.id)
		) {
			const tabHolder = document.getElementsByClassName('tab active')[0];
			if (tabHolder) tabHolder.scrollTo({ top: 0, behavior: 'auto' });
		}
	}, [mediaItem, presentedMedia]);

	const shownTeleprompterSections = useMemo(() => {
		if (!selectedTeleprompter) {
			return [];
		}

		if (mediaItem) {
			const mediaPages = mediaItem.images?.split(',') ?? [];
			// create sections for all media pages for media item
			return mediaPages.map(
				(page: string, index: number): TeleprompterSectionInterface => {
					// see if there's existing teleprompter section for this page
					const existingSection = selectedTeleprompter?.sections.find(
						(section: TeleprompterSectionInterface) => section.section_type_id === mediaItem.id
						&& section.section_data?.media_url === page,
					);

					const newSection = {
						id: existingSection?.id ?? index + 1,
						teleprompter_id: selectedTeleprompter?.id ?? 0,
						section_data: {
							section_title: `Slide ${index + 1}`,
							shotObject: null,
							media_url: page,
							hasContent: existingSection?.section_data.hasContent
								? existingSection?.section_data.hasContent
								: false,
						},
						content: existingSection ? existingSection.content : '',
						section_type_id: mediaItem.id,
						section_type: TELEPROMPTER_SECTION_TYPES.MEDIA,
					};

					return newSection;
				},
			);
		}
		// create sections for all shots in room
		return shots.map(
			(shot: Shot, index: number): TeleprompterSectionInterface => {
				// see if there's existing teleprompter section for this shot
				const existingSection = selectedTeleprompter?.sections.find(
					(section: any) => section.section_type_id === shot.shot_id
					&& section.section_type === TELEPROMPTER_SECTION_TYPES.SHOT,
				);

				const newSection = {
					id: existingSection?.id ?? index + 1,
					teleprompter_id: selectedTeleprompter?.id ?? 0,
					section_data: {
						section_title: shot.shot_data.shotTitle,
						shotObject: shot,
						media_url: null,
						hasContent: existingSection?.section_data.hasContent
							? existingSection?.section_data.hasContent
							: false,
					},
					content: existingSection ? existingSection.content : '',
					section_type_id: shot.shot_id,
					section_type: TELEPROMPTER_SECTION_TYPES.SHOT,
				};

				return newSection;
			},
		);
	}, [mediaItem, shots, selectedTeleprompter]);

	const goBack = () => {
		if (mediaItem) {
			dispatch(TeleprompterActions.setSelectedTeleprompters(['media', null]));
		} else {
			dispatch(TeleprompterActions.setSelectedTeleprompters(['shot', null]));
		}
	};

	const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
		setTeleprompterTitle(e.target.value);

		if (selectedTeleprompter) {
			editTeleprompter({ ...selectedTeleprompter, title: e.target.value });
		}
	};

	const isInCanvasOrPlaying = (
		isMedia: boolean,
		sectionTypeId: number,
		mediaPage?: number,
	) => {
		if (isMedia) {
			const mediaIsInCanvas = presentedMedia.find(
				(item: PresentedMediaItem) => item.mediaId === sectionTypeId,
			);
			if (mediaIsInCanvas && mediaIsInCanvas.currentPage === mediaPage) {
				return shotStartTimestampEntity
					? TELEPROMPTER_SECTION_STATES.PLAYING
					: TELEPROMPTER_SECTION_STATES.SELECTED;
			}
			return TELEPROMPTER_SECTION_STATES.NOT_SELECTED;
		}
		if (!isMedia && sectionTypeId === currentShotId) {
			return shotStartTimestampEntity
				? TELEPROMPTER_SECTION_STATES.PLAYING
				: TELEPROMPTER_SECTION_STATES.SELECTED;
		}
		return TELEPROMPTER_SECTION_STATES.NOT_SELECTED;
	};

	const canEdit = useMemo(
		() => selectedTeleprompter?.gr_user === user?.gr_user
		|| (
			// slide notes have the same editing conditions as media
			!!selectedTeleprompter?.media_id && !!mediaItem && !!user
			&& (host || listToArray(mediaItem.shared_with ?? '').includes(user.gr_user))
		),
		[selectedTeleprompter, user, host, mediaItem],
	);

	return (
		<>
			{selectedTeleprompter && (
				<div className="teleprompter-wrapper position-absolute width-full position-top background-medium" id="teleprompter-wrapper">
					<div className="margin-bottom-small justify-content-space-between">
						<div className="teleprompter-header flex-align-center width-full overflow-hidden padding-right-medium">
							<button className="back background-color-none" onClick={() => goBack()}><Icon name={ICONS.ARROW_LEFT} size={16} color={COLORS.LIGHT_GRAY} /></button>
							{mediaItem || !canEdit
								? <span className="truncate-overflow">{mediaItem ? mediaItem.custom_name || mediaItem.original_name : teleprompterTitle || 'Untitled'}</span>
								: <input className="truncate-overflow" type="text" placeholder="Give this Shot Note a Name" value={teleprompterTitle} onChange={(e) => setTeleprompterTitle(e.target.value)} onBlur={handleChangeTitle} />
							}
						</div>
						<div className="note-sharebar">
							<ShareWith
								shareType={
									mediaItem
										? SHARE_TYPE.SLIDE_NOTE
										: SHARE_TYPE.SHOT_NOTE
								}
								popoverToggled={() => { }}
								socket={socket}
								popoverClosed={() => { }}
								forNote={true}
								forGlobalNote={false}
							/>
						</div>
					</div>
					{shownTeleprompterSections.map(
						(
							teleprompterSection: TeleprompterSectionInterface,
							index: number,
						) => (
							<TeleprompterSection
								key={`${teleprompterSection.section_type_id}${index}`}
								teleprompterId={selectedTeleprompter?.id ?? 0}
								teleprompterSection={teleprompterSection}
								selectedSection={
									teleprompterSection.section_type === TELEPROMPTER_SECTION_TYPES.SHOT
										? selectedSection === teleprompterSection.section_type_id
										: selectedSection === teleprompterSection.section_data.media_url
								}
								setSelectedSection={(id: number | string) => setSelectedSection(id)}
								setMediaItem={(item: MediaItem) => {
									handleSetMediaTeleprompter(item);
								}}
								mediaItem={mediaItem}
								sectionState={
									teleprompterSection.section_type === TELEPROMPTER_SECTION_TYPES.SHOT
										? (
											isInCanvasOrPlaying(
												false,
												teleprompterSection.section_type_id,
											)
										)
										: (
											isInCanvasOrPlaying(
												true,
												teleprompterSection.section_type_id,
												index + 1,
											)
										)
								}
								socket={socket}
								canEdit={canEdit}
								content={cleanContent(teleprompterSection.content)}
							/>
						),
					)}
				</div>
			)}
		</>
	);
}
